import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { usePrice } from '../hooks/usePrice';

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatPrice } = usePrice();
  const page = parseInt(searchParams.get('page') || '1');
  const categoryId = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const sortBy = searchParams.get('sortBy') || 'createdAt';

  const { data, isLoading } = useQuery({
    queryKey: ['products', page, categoryId, search, sortBy],
    queryFn: () => api.get(`/products?page=${page}&limit=20&categoryId=${categoryId}&search=${search}&sortBy=${sortBy}`),
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/products/categories'),
  });

  const products = (data as any)?.data?.products || [];
  const pagination = (data as any)?.data?.pagination;
  const categories = (categoriesData as any)?.data?.categories || [];

  const updateParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    if (key !== 'page') newParams.set('page', '1');
    setSearchParams(newParams);
  };

  return (
    <div className="py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-western text-4xl text-cream mb-2">Products</h1>
          <p className="text-text-secondary">Browse our collection of digital goods</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <div className="card p-6 sticky top-24">
              <h2 className="font-semibold text-cream mb-4">Filters</h2>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm text-text-secondary mb-2">Search</label>
                <input
                  type="text"
                  className="input text-sm"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => updateParam('search', e.target.value)}
                />
              </div>

              {/* Categories */}
              <div className="mb-6">
                <label className="block text-sm text-text-secondary mb-2">Category</label>
                <select
                  className="input text-sm"
                  value={categoryId}
                  onChange={(e) => updateParam('category', e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm text-text-secondary mb-2">Sort By</label>
                <select
                  className="input text-sm"
                  value={sortBy}
                  onChange={(e) => updateParam('sortBy', e.target.value)}
                >
                  <option value="createdAt">Newest</option>
                  <option value="price">Price: Low to High</option>
                  <option value="salesCount">Best Selling</option>
                  <option value="ratingAverage">Top Rated</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="card animate-pulse">
                    <div className="aspect-video bg-leather" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-leather rounded w-1/3" />
                      <div className="h-5 bg-leather rounded w-2/3" />
                      <div className="h-4 bg-leather rounded w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-text-secondary mb-4">No products found</p>
                <button
                  onClick={() => setSearchParams({})}
                  className="text-gold hover:text-gold-bright"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product: any) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.id}`}
                      className="card group overflow-hidden"
                    >
                      <div className="aspect-video bg-leather relative overflow-hidden">
                        {product.thumbnailUrl ? (
                          <img
                            src={product.thumbnailUrl}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-gold/30 font-western text-2xl">OO</span>
                          </div>
                        )}
                        {product.compareAtPrice && (
                          <span className="absolute top-2 right-2 bg-hot text-white text-xs px-2 py-1 rounded">
                            Sale
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <p className="text-text-muted text-xs mb-1">{product.seller.storeName}</p>
                        <h3 className="font-medium text-cream mb-2 line-clamp-1 group-hover:text-gold transition-colors">
                          {product.name}
                        </h3>
                        {product.shortDescription && (
                          <p className="text-text-secondary text-sm mb-3 line-clamp-2">
                            {product.shortDescription}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-gold font-semibold">{formatPrice(product.price)}</span>
                            {product.compareAtPrice && (
                              <span className="text-text-muted line-through text-sm">
                                {formatPrice(product.compareAtPrice)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-text-muted text-sm">
                            <span>&#9733;</span>
                            <span>{product.ratingAverage?.toFixed(1) || '0.0'}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      onClick={() => updateParam('page', String(page - 1))}
                      disabled={page <= 1}
                      className="btn-secondary py-2 px-4 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-text-secondary px-4">
                      Page {page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => updateParam('page', String(page + 1))}
                      disabled={page >= pagination.totalPages}
                      className="btn-secondary py-2 px-4 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
